import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = d.slice(0, 10);
  const [y, m, day] = s.split('-');
  return day && m && y ? day + '/' + m + '/' + y : '—';
};
const fmtEur = v => Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC';

const STATUS_LABEL = {
  planned: 'Pianificata', in_progress: 'In corso', at_risk: 'A rischio',
  done: 'Completata', blocked: 'Bloccata',
};
const BADGE_STYLES = {
  done:        { backgroundColor: '#E8F5EF', color: '#0F6E56' },
  in_progress: { backgroundColor: '#EEF3FF', color: '#2D4BA0' },
  at_risk:     { backgroundColor: '#FEF6E7', color: '#8B5E00' },
  planned:     { backgroundColor: '#F5F5F5', color: '#555' },
  blocked:     { backgroundColor: '#FDEEEE', color: '#9B1C1C' },
};

const s = StyleSheet.create({
  page: { paddingTop: 71, paddingBottom: 71, paddingLeft: 40, paddingRight: 40, fontSize: 8, fontFamily: 'Helvetica', color: '#1a1a2e' },
  headerBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: '#2EA084' },
  headerWrap: { position: 'absolute', top: 12, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 8, borderBottom: '1pt solid #e9ecef' },
  headerLeft: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#2EA084' },
  headerRight: { fontSize: 8, color: '#868e96', textAlign: 'right' },
  footer: { position: 'absolute', bottom: 24, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#868e96', borderTop: '0.5pt solid #e9ecef', paddingTop: 6 },
  sectionHead: { borderLeft: '3pt solid #2EA084', paddingLeft: 8, paddingTop: 4, paddingBottom: 4, fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#0A1628', backgroundColor: '#fff', marginBottom: 6, marginTop: 14 },
  kpiRow: { flexDirection: 'row', marginBottom: 8 },
  kpiBox: { border: '1pt solid #e9ecef', borderRadius: 4, padding: '8 10', flex: 1, margin: '0 4' },
  kpiLabel: { fontSize: 7, color: '#868e96', textTransform: 'uppercase', marginBottom: 2 },
  kpiValue: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  tableHead: { flexDirection: 'row', backgroundColor: '#f8f9fa', borderBottom: '1pt solid #e9ecef', padding: '4 6', fontFamily: 'Helvetica-Bold', fontSize: 7 },
  tableRow: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8 },
  tableRowAlt: { flexDirection: 'row', borderBottom: '0.5pt solid #e9ecef', padding: '4 6', fontSize: 8, backgroundColor: '#f8f9fa' },
  twoCol: { flexDirection: 'row', gap: 20 },
  col: { flex: 1 },
  row: { flexDirection: 'row', justifyContent: 'space-between', padding: '3 0', borderBottom: '0.5pt solid #e9ecef' },
  rowLabel: { fontSize: 8, color: '#868e96' },
  rowValue: { fontSize: 8, color: '#1a1a2e' },
  badge: { fontSize: 7, padding: '1 4', borderRadius: 2 },
});

export default function PanoramicaSintesiPDF({ data }) {
  const oggi = new Date().toLocaleDateString('it-IT');
  const cant = data?.cantiere || {};
  const az = data?.azienda || {};
  const p = data?.piano_lavori || {};
  const b = data?.budget || {};
  const c = data?.contatori || {};
  const ur = data?.ultimo_report;
  const fasi = p.fasi || [];
  const milestone = data?.milestone || [];

  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.headerBar} fixed />
        <View style={s.headerWrap} fixed>
          <Text style={s.headerLeft}>Scheda Cantiere — {cant.titolo || ''}</Text>
          <View style={s.headerRight}>
            <Text>{az.nome || ''}</Text>
            <Text>Generato il {oggi}</Text>
          </View>
        </View>

        {/* Anagrafica */}
        <Text style={s.sectionHead}>Anagrafica</Text>
        <View style={s.twoCol}>
          <View style={s.col}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>Cantiere</Text>
            <Row label="Titolo" value={cant.titolo} />
            <Row label="Indirizzo" value={cant.indirizzo} />
            <Row label="Stato" value={cant.status} />
            <Row label="Inizio" value={fmtDate(cant.data_inizio)} />
            <Row label="Fine stimata" value={fmtDate(cant.data_fine_stimata)} />
          </View>
          <View style={s.col}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>Cliente</Text>
            <Row label="Nome" value={cant.cliente_nome} />
          </View>
        </View>

        {/* Numeri chiave */}
        <Text style={s.sectionHead}>Numeri chiave</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Avanzamento</Text><Text style={[s.kpiValue, { color: '#2EA084' }]}>{p.percentuale_avanzamento || 0}%</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Budget totale</Text><Text style={s.kpiValue}>{fmtEur(b.budget_totale)}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Speso</Text><Text style={[s.kpiValue, { color: '#E85D5D' }]}>{fmtEur(b.budget_speso)}</Text></View>
        </View>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Valore commessa</Text><Text style={s.kpiValue}>{fmtEur(b.valore_commessa)}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Fatturato</Text><Text style={[s.kpiValue, { color: '#2EA084' }]}>{fmtEur(b.fatturato)}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Da incassare</Text><Text style={[s.kpiValue, { color: '#F5A623' }]}>{fmtEur(b.da_incassare)}</Text></View>
        </View>

        {/* Piano lavori */}
        <Text style={s.sectionHead}>Piano lavori</Text>
        {p.esiste && fasi.length > 0 ? (
          <>
            <View style={s.tableHead}>
              <Text style={{ flex: 3 }}>Fase</Text>
              <Text style={{ flex: 1.5 }}>Stato</Text>
              <Text style={{ flex: 1 }}>Inizio</Text>
              <Text style={{ flex: 1 }}>Fine</Text>
              <Text style={{ flex: 0.8, textAlign: 'right' }}>%</Text>
            </View>
            {fasi.map((f, i) => (
              <View key={f.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                <Text style={{ flex: 3 }}>{f.nome}</Text>
                <View style={{ flex: 1.5 }}>
                  <Text style={[s.badge, BADGE_STYLES[f.status] || BADGE_STYLES.planned, { alignSelf: 'flex-start' }]}>{STATUS_LABEL[f.status] || f.status}</Text>
                </View>
                <Text style={{ flex: 1 }}>{fmtDate(f.data_inizio_pianificata)}</Text>
                <Text style={{ flex: 1 }}>{fmtDate(f.data_fine_pianificata)}</Text>
                <Text style={{ flex: 0.8, textAlign: 'right' }}>{f.percentuale_completamento || 0}%</Text>
              </View>
            ))}
          </>
        ) : (
          <Text style={{ fontSize: 8, color: '#868e96', padding: '6 0' }}>Piano lavori non configurato</Text>
        )}

        {/* Milestone */}
        {milestone.length > 0 && (
          <>
            <Text style={s.sectionHead}>Milestone</Text>
            {milestone.map((m, i) => (
              <View key={m.id} style={i % 2 === 0 ? s.tableRow : s.tableRowAlt}>
                <Text style={{ flex: 3 }}>{m.nome}</Text>
                <Text style={{ flex: 1.5 }}>{fmtDate(m.data)}</Text>
                <Text style={{ flex: 1, color: m.raggiunta ? '#2EA084' : '#868e96' }}>{m.raggiunta ? 'Raggiunta' : 'In attesa'}</Text>
              </View>
            ))}
          </>
        )}

        {/* Ultimo report */}
        <Text style={s.sectionHead}>Ultimo report</Text>
        {ur ? (
          <View>
            <Row label="Data" value={fmtDate(ur.data)} />
            <Row label="Avanzamento SAL" value={ur.avanzamento_sal + '%'} />
            <Row label="Problemi aperti" value={String(ur.problemi_aperti)} />
          </View>
        ) : (
          <Text style={{ fontSize: 8, color: '#868e96', padding: '6 0' }}>Nessun report registrato</Text>
        )}

        {/* Contatori */}
        <Text style={s.sectionHead}>Documenti e spese</Text>
        <View style={s.kpiRow}>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Documenti</Text><Text style={s.kpiValue}>{c.documenti || 0}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Vocali</Text><Text style={s.kpiValue}>{c.vocali_non_letti || 0}</Text></View>
          <View style={s.kpiBox}><Text style={s.kpiLabel}>Fatture aperte</Text><Text style={s.kpiValue}>{c.fatture_aperte || 0}</Text></View>
        </View>

        <View style={s.footer} fixed>
          <Text>mAIstro — Scheda Cantiere</Text>
          <Text>{cant.titolo || ''}</Text>
          <Text render={({ pageNumber, totalPages }) => 'Pagina ' + pageNumber + ' di ' + totalPages} />
        </View>
      </Page>
    </Document>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <View style={s.row}>
      <Text style={s.rowLabel}>{label}</Text>
      <Text style={s.rowValue}>{value}</Text>
    </View>
  );
}
